/**
 * WebSocket Service for Real-time Updates
 * Handles connection, reconnection, and event broadcasting
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.listeners = new Map();
    this.isConnected = false;
    this.pendingMessages = [];
    this.pingInterval = null;
    this.rooms = ['dashboard', 'notifications'];
  }

  /**
   * Connect to WebSocket server
   */
  connect(token, rooms = ['dashboard', 'notifications']) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    this.rooms = rooms;
    const wsUrl = process.env.REACT_APP_BACKEND_URL
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    
    const roomsParam = rooms.join(',');
    const url = `${wsUrl}/ws?token=${token}&rooms=${roomsParam}`;

    try {
      this.ws = new WebSocket(url);
      
      this.ws.onopen = () => this.handleOpen();
      this.ws.onclose = (event) => this.handleClose(event, token);
      this.ws.onerror = (error) => this.handleError(error);
      this.ws.onmessage = (event) => this.handleMessage(event);
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }

  /**
   * Handle connection open
   */
  handleOpen() {
    console.log('WebSocket connected');
    this.isConnected = true;
    this.reconnectAttempts = 0;
    
    // Start ping interval to keep connection alive
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000);
    
    // Send pending messages
    while (this.pendingMessages.length > 0) {
      const msg = this.pendingMessages.shift();
      this.send(msg);
    }
    
    // Notify listeners
    this.emit('connected', { rooms: this.rooms });
  }

  /**
   * Handle connection close
   */
  handleClose(event, token) {
    console.log('WebSocket disconnected:', event.code, event.reason);
    this.isConnected = false;
    
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    // Attempt reconnection
    if (this.reconnectAttempts < this.maxReconnectAttempts && event.code !== 4001) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      setTimeout(() => {
        this.connect(token, this.rooms);
      }, delay);
    }
    
    this.emit('disconnected', { code: event.code, reason: event.reason });
  }

  /**
   * Handle connection error
   */
  handleError(error) {
    console.error('WebSocket error:', error);
    this.emit('error', { error });
  }

  /**
   * Handle incoming message
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      
      // Handle different message types
      switch (data.type) {
        case 'pong':
          // Ping response, connection is alive
          break;
          
        case 'connected':
          console.log('WebSocket authenticated:', data);
          break;
          
        case 'work_order_update':
          this.emit('work_order_update', data);
          this.emit('dashboard_refresh', { reason: 'work_order' });
          break;
          
        case 'inspection_update':
          this.emit('inspection_update', data);
          this.emit('dashboard_refresh', { reason: 'inspection' });
          break;
          
        case 'asset_update':
          this.emit('asset_update', data);
          break;
          
        case 'dashboard_refresh':
          this.emit('dashboard_refresh', data);
          break;
          
        case 'notification':
          this.emit('notification', data);
          break;
          
        case 'room_joined':
        case 'room_left':
          console.log(`Room ${data.type}:`, data.room);
          break;
          
        default:
          // Emit generic event
          this.emit(data.type, data);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Send message through WebSocket
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      // Queue message for when connection is established
      this.pendingMessages.push(data);
    }
  }

  /**
   * Join a room
   */
  joinRoom(room) {
    this.send({ type: 'join_room', room });
    if (!this.rooms.includes(room)) {
      this.rooms.push(room);
    }
  }

  /**
   * Leave a room
   */
  leaveRoom(room) {
    this.send({ type: 'leave_room', room });
    this.rooms = this.rooms.filter(r => r !== room);
  }

  /**
   * Subscribe to specific work order updates
   */
  subscribeToWorkOrder(workOrderId) {
    this.send({ type: 'subscribe_work_order', work_order_id: workOrderId });
  }

  /**
   * Add event listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to all listeners
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} listener:`, error);
        }
      });
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.isConnected = false;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent auto-reconnect
  }

  /**
   * Get connection status
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      rooms: this.rooms,
      readyState: this.ws ? this.ws.readyState : null
    };
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
export default wsService;
